import { observable, action, configure, makeObservable } from 'mobx';
import { IEdetector } from '@utils/browserUTLs';
import type {
  ShopFragmentCartEndpointFragment,
  OrderItemFragmentFragment
} from '@schema/cart';
import API from '@api';
import { queryClient } from '@root/src/providers/query';
import { shopsQueryOptions } from '@queries/shops';
import { barMenuTodayOrdersQueryOptions } from '@queries/peopleOrders';

configure({
  enforceActions: 'observed',
  useProxies: IEdetector() ? 'never' : 'always'
});

let orders: OrderItemFragmentFragment[] | null = null;

const BAR_MENU_CANCELED_STATUS_IDS = [9];

const withoutCanceledOrders = (
  list: OrderItemFragmentFragment[]
): OrderItemFragmentFragment[] =>
  list.filter(
    order => !BAR_MENU_CANCELED_STATUS_IDS.includes(order.status_id ?? -1)
  );

const ORDER_CREATED_STORAGE_KEY = 'barMenuOrderCreatedId';
const ORDER_CREATED_TTL_MS = 24 * 60 * 60 * 1000;

const getSavedOrderCreated = (): { id: string; key: string | null } | null => {
  // ЭТАП 1 · Шаг 3 — SSR-safe: вызывается на module-eval, на сервере нет localStorage.
  if (typeof localStorage === 'undefined') return null;
  try {
    const saved = localStorage.getItem(ORDER_CREATED_STORAGE_KEY);
    if (!saved) {
      return null;
    }
    const { id, key, ts } = JSON.parse(saved) as {
      id?: string;
      key?: string;
      ts?: number;
    };
    if (!id || !ts || Date.now() - ts > ORDER_CREATED_TTL_MS) {
      localStorage.removeItem(ORDER_CREATED_STORAGE_KEY);
      return null;
    }
    return { id, key: key || null };
  } catch (error) {
    console.log(error);
    return null;
  }
};

const saveOrderCreated = (orderId: string | null, key: string | null): void => {
  if (typeof localStorage === 'undefined') return;
  try {
    if (orderId) {
      localStorage.setItem(
        ORDER_CREATED_STORAGE_KEY,
        JSON.stringify({ id: orderId, key: key || undefined, ts: Date.now() })
      );
    } else {
      localStorage.removeItem(ORDER_CREATED_STORAGE_KEY);
    }
  } catch (error) {
    console.log(error);
  }
};

interface SavedGuestOrder {
  id: number;
  key: string;
  ts: number;
}

const GUEST_ORDERS_STORAGE_KEY = 'barMenuGuestOrders';

const getSavedGuestOrders = (): SavedGuestOrder[] => {
  if (typeof localStorage === 'undefined') return [];
  try {
    const saved = localStorage.getItem(GUEST_ORDERS_STORAGE_KEY);
    if (!saved) {
      return [];
    }
    const parsed = JSON.parse(saved) as SavedGuestOrder[];
    return parsed.filter(
      order =>
        order?.id &&
        order?.key &&
        order?.ts &&
        Date.now() - order.ts <= ORDER_CREATED_TTL_MS
    );
  } catch (error) {
    console.log(error);
    return [];
  }
};

const saveGuestOrder = (id: number, key: string): void => {
  if (typeof localStorage === 'undefined') return;
  try {
    const rest = getSavedGuestOrders().filter(order => order.id !== id);
    localStorage.setItem(
      GUEST_ORDERS_STORAGE_KEY,
      JSON.stringify([{ id, key, ts: Date.now() }, ...rest].slice(0, 10))
    );
  } catch (error) {
    console.log(error);
  }
};

const guestOrdersCache = new Map<number, OrderItemFragmentFragment>();

const fetchGuestOrders = async (): Promise<OrderItemFragmentFragment[]> => {
  const saved = getSavedGuestOrders();
  if (!saved.length) {
    return [];
  }
  const guestOrders = await Promise.all(
    saved.map(async ({ id, key }) => {
      const cached = guestOrdersCache.get(id);
      if (cached?.paid === 'Y') {
        return cached;
      }
      return await API.confirmOrderById({ orderId: id, hash: key })
        .then(res => {
          if (!res.confirmOrderById) {
            return null;
          }
          const order = {
            ...res.confirmOrderById,
            secretKey: key
          } as unknown as OrderItemFragmentFragment;
          guestOrdersCache.set(id, order);
          return order;
        })
        .catch(error => {
          console.log(error);
          return null;
        });
    })
  );
  return guestOrders.filter(Boolean) as OrderItemFragmentFragment[];
};

const savedOrderCreated = getSavedOrderCreated();

class Store {
  last10BarMenuOrders: OrderItemFragmentFragment[] = [];

  orderCreatedId: string | null = savedOrderCreated?.id ?? null;

  orderCreatedKey: string | null = savedOrderCreated?.key ?? null;

  shouldShowOrderPayedId: string | null = null;

  currentStore: ShopFragmentCartEndpointFragment | null = null;

  isCurrentStoreLoading: boolean = false;

  allStores: ShopFragmentCartEndpointFragment[] = [];

  constructor() {
    makeObservable(this, {
      last10BarMenuOrders: observable,
      orderCreatedId: observable,
      orderCreatedKey: observable,
      shouldShowOrderPayedId: observable,
      currentStore: observable,
      isCurrentStoreLoading: observable,
      allStores: observable,
      getCurrentStore: action,
      setIsCurrentStoreLoading: action,
      setCurrentStore: action,
      setShouldShowOrderPayedId: action,
      setOrderCreatedId: action,
      setLast10BarMenuOrders: action,
      getLast10BarMenuOrders: action,
      setAllStores: action
    });
  }

  setCurrentStore = (store: ShopFragmentCartEndpointFragment | null): void => {
    this.currentStore = store;
  };

  setAllStores = (stores: ShopFragmentCartEndpointFragment[]): void => {
    this.allStores = stores;
  };

  getCurrentStore = async ({ barId }: { barId: string }): Promise<void> => {
    if (!barId || this.isCurrentStoreLoading) {
      return;
    }
    this.setIsCurrentStoreLoading(true);
    await queryClient
      .ensureQueryData(shopsQueryOptions())
      .then(res => {
        const allShops = (res.getShops ||
          []) as unknown as ShopFragmentCartEndpointFragment[];
        this.setAllStores(allShops);
        this.setIsCurrentStoreLoading(false);
        const bar = allShops.find(item => item.id === +barId);
        if (!bar) {
          return null;
        }
        this.setCurrentStore(bar);
      })
      .catch(err => {
        this.setIsCurrentStoreLoading(false);
        this.setCurrentStore(null);
        console.log(err);
      });
  };

  setIsCurrentStoreLoading = (isLoading: boolean): void => {
    this.isCurrentStoreLoading = isLoading;
  };

  getLast10BarMenuOrders = async (force = false): Promise<void> => {
    const options = barMenuTodayOrdersQueryOptions();
    try {
      const data = force
        ? await queryClient.fetchQuery({ ...options, staleTime: 0 })
        : await queryClient.fetchQuery(options);
      if (!data) {
        this.setLast10BarMenuOrders(await fetchGuestOrders());
        return;
      }
      const ordersRes = data.ordersList || [];
      const previousOrders = orders;
      if (previousOrders !== null) {
        ordersRes.forEach(order => {
          const previous = previousOrders.find(a => a.id === order.id);
          if (previous && order.paid === 'Y' && previous.paid !== 'Y') {
            this.setShouldShowOrderPayedId(order.id);
          }
        });
      }
      orders = ordersRes;
      this.setLast10BarMenuOrders(ordersRes);
    } catch (error) {
      console.log(error);
    }
  };

  setShouldShowOrderPayedId = (orderId: string | null): void => {
    this.shouldShowOrderPayedId = orderId;
  };

  setLast10BarMenuOrders = (orders: OrderItemFragmentFragment[]): void => {
    this.last10BarMenuOrders = withoutCanceledOrders(orders);
  };

  setOrderCreatedId = (
    orderId: string | null,
    key: string | null = null
  ): void => {
    this.orderCreatedId = orderId;
    this.orderCreatedKey = orderId ? key : null;
    saveOrderCreated(orderId, orderId ? key : null);
    if (orderId && key) {
      saveGuestOrder(+orderId, key);
    }
  };
}

const barMenuStore = new Store();
export type BarMenuStoreT = typeof barMenuStore;
export default barMenuStore;
